/**
 * Public pricing in USD.
 *
 * Euro → USD ≈ 1.08 (rounded for clean display).
 * AI Receptionist / Farori: PKR 5,300 → ≈ $19 (≈278 PKR/USD, Jul 2026).
 *
 * Prices cover all branches for now (not per-branch).
 */

export type PricingModuleId =
  | 'pos'
  | 'kds'
  | 'waiter'
  | 'reservations'
  | 'riders'
  | 'inventory'
  | 'accounting'
  | 'website'
  | 'analytics'
  | 'ai_receptionist'
  | 'ai_assistant'

export type PricingModule = {
  id: PricingModuleId
  name: string
  priceUsd: number
  /** Required module ids that auto-enable when this is on */
  needs?: PricingModuleId[]
  /** Locked on — cannot turn off */
  locked?: boolean
  noTrial?: boolean
  note?: string
}

/** Individual modules (calculator). */
export const PRICING_MODULES: PricingModule[] = [
  {
    id: 'pos',
    name: 'POS Core',
    priceUsd: 11,
    locked: true,
    note: 'Required base module',
  },
  {
    id: 'kds',
    name: 'Kitchen Display',
    priceUsd: 5,
    needs: ['pos'],
    note: 'Needs POS',
  },
  {
    id: 'waiter',
    name: 'Waiter App',
    priceUsd: 5,
    needs: ['pos'],
    note: 'Needs POS',
  },
  {
    id: 'reservations',
    name: 'Reservations',
    priceUsd: 5,
    needs: ['pos'],
    note: 'Needs POS',
  },
  {
    id: 'riders',
    name: 'Rider App',
    priceUsd: 11,
  },
  {
    id: 'inventory',
    name: 'Inventory',
    priceUsd: 11,
  },
  {
    id: 'accounting',
    name: 'Accounting',
    priceUsd: 22,
  },
  {
    id: 'website',
    name: 'Website & Ordering',
    priceUsd: 11,
  },
  {
    id: 'analytics',
    name: 'Website Analytics',
    priceUsd: 11,
    needs: ['website'],
    note: 'Needs Website',
  },
  {
    id: 'ai_receptionist',
    name: 'AI Receptionist (WhatsApp)',
    priceUsd: 19,
    noTrial: true,
    note: 'No trial — paid from day one',
  },
  {
    id: 'ai_assistant',
    name: 'AI Assistant (Farori)',
    priceUsd: 19,
    note: 'In-app helper for owners & staff',
  },
]

export const DEFAULT_SELECTED_MODULES: PricingModuleId[] = [
  'pos',
  'kds',
  'waiter',
  'riders',
  'inventory',
  'accounting',
  'website',
  'analytics',
  'ai_receptionist',
  'ai_assistant',
]

export function formatUsd(amount: number): string {
  return `$${amount}`
}

export function sumModules(ids: Iterable<PricingModuleId>): number {
  const set = new Set(ids)
  return PRICING_MODULES.filter((m) => set.has(m.id)).reduce((sum, m) => sum + m.priceUsd, 0)
}

export const ALL_MODULES_TOTAL_USD = sumModules(PRICING_MODULES.map((m) => m.id))
